import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import CardParcelModalWindow from '../modals/CardParcelModalWindow/CardParcelModalWindow';
import cn from 'classnames';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import { Alert, AlertType } from '@components/new/Alert';
import { Column, Row } from '@components/Grid';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { getDaysBetweenDates } from '@utils/progress';
import {
  getLastMileResale,
  getLastMileWarehouses,
  TLastMileResaleParcel,
  TLastMileWarehouse
} from '@api/apiClient/returns';
import { ILastMileStockTabProps } from '../types';
import { Modal } from '@components/Modal';
import { sortByStores } from '@utils/sorting';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

const LastMileStockTab: React.FC<ILastMileStockTabProps> = (props: ILastMileStockTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen
  } = props;

  const [columnsSort, setColumnsSort] = useState<string[]>([]);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [parcel, setParcel] = useState<TLastMileResaleParcel | undefined>(undefined);

  const dataRef = useRef<DataViewHandle>(null);

  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  const {
    data: warehousesData,
    isError: isWarehousesError,
    isFetching: isWarehousesFetching
  } = useQuery({
    queryKey: ['lastMileWarehouses'],
    queryFn: getLastMileWarehouses,
    retry: 1
  });

  const warehousesWithIncorrectDropOff: TLastMileWarehouse[] =
      isWarehousesFetching || isWarehousesError
        ? []
        : warehousesData?.warehouses.filter((warehouse) => warehouse.warningReason === 'IncorrectDropOff') ?? [];

  const handlerSortByStores = (position: number, value: string, filters: FilterModel[]): void => {
    sortByStores({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const columns: TableColumn<TLastMileResaleParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => (
        <TrackingNumber
          externalId={row.data?.trackingNumber ?? undefined}
          trackNumber={row.data?.trackingNumber ?? undefined}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcel(row.data);
          }}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '175px'
    },
    {
      header: t('returnsPage.lastMileStockTab.name'),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => (
          <a
              className={styles.link}
              href={row?.data?.url}
              rel="noreferrer"
              target="_blank"
          >
            {row?.data?.title}
          </a>
      ),
      width: '235px'
    },
    {
      header: t('returnsPage.lastMileStockTab.vendorCode'),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => row.data.vendorCode || '—',
      noOverflow: true,
      type: TableColumnType.TERTIARY
    },
    {
      header: t('returnsPage.lastMileStockTab.store'),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => (
          <AvatarWithMarketplace
              id={row.data.seller.id}
              name={row.data.seller.name}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByStores
    },
    {
      header: t('returnsPage.lastMileStockTab.stockedAt'),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => row.data.stockedAt ? formatDate(row.data.stockedAt, 'dd.MM.yy') : '—',
      type: TableColumnType.TERTIARY
    },
    {
      header: t('returnsPage.lastMileStockTab.awaitingTo'),
      accessor: (row: TableRowType<TLastMileResaleParcel>) => {
        if (!row.data.decisionDeadline) return '—';
        const restDays = getDaysBetweenDates(null, row.data.decisionDeadline);
        return (
            <Text
                color={restDays <= 3 ? TextColor.ERROR_DARK : TextColor.PRIMARY}
                size={TextSize.S}
            >
              {formatDate(row.data.decisionDeadline, 'dd.MM.yy')}
            </Text>
        );
      },
      noOverflow: true
    }
  ], [currentLanguage, columnsSort]);

  useEffect(() => {
    const columnsSort: string[] = columns.map((item: TableColumn<TLastMileResaleParcel>) => item.sortable ? 'asc' : '');
    setColumnsSort(columnsSort);
  }, []);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.lastMileStockTab.info_1')}
          </Text>
        </Box>
        {warehousesWithIncorrectDropOff.length ? (
            <Box pb={5} className={styles.alerts}>
              {warehousesWithIncorrectDropOff.map((warehouse: TLastMileWarehouse) => (
                  <Alert
                      key={warehouse.id}
                      type={AlertType.ERROR}
                      title={t('returnsPage.lastMileStockTab.incorrectDropOffTitle')}
                      description={(
                          <span className={styles.alertDescription}>
                            <span className={styles.alertDescriptionLine}>
                              {t('returnsPage.lastMileStockTab.incorrectDropOffChange', { warehouseName: warehouse.name })}
                            </span>
                            <span className={styles.alertDescriptionLine}>
                              {t('returnsPage.lastMileStockTab.incorrectDropOffFuture')}
                            </span>
                          </span>
                      )}
                      isWide
                  />
              ))}
            </Box>
        ) : null}
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="returns-last-mile-stock"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TLastMileResaleParcel>
            columns={columns}
            columnsSort={columnsSort}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getLastMileResale}
            notFound={t('returnsPage.lastMileStockTab.noParcels')}
            pending={!isInitialized}
            queryKey="lastMileResaleParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={() => setIsOpenParcelWindow(false)}
            onClose={() => setIsOpenParcelWindow(false)}
            width={900}
        >
          <CardParcelModalWindow
              callFrom="lastMileStockTab"
              handlerSetIsOpenParcelWindow={setIsOpenParcelWindow}
              parcel={parcel!}
          />
        </Modal>
      </div>
  );
};

export default LastMileStockTab;
